import { useCallback, useEffect, useRef, useState } from "react";
import {
  downloadResultPdf,
  getResultPdfStatus,
} from "../services/printStudentSheetsApi";

const POLL_INTERVAL_MS = 3000;
const PDF_PROGRESS_PROCESSING = 95;

interface UsePrintStudentSheetsOptions {
  csvName?: string;
  isMarkingLoading?: boolean;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export const usePrintStudentSheets = ({
  csvName,
  isMarkingLoading = false,
  onSuccess,
  onError,
}: UsePrintStudentSheetsOptions) => {
  const [isPrintLoading, setPrintLoading] = useState(false);
  const [isPdfGenerating, setPdfGenerating] = useState(false);
  const [printProgress, setPrintProgress] = useState(0);
  const [pdfReady, setPdfReady] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failedNotifiedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const clearCompleteTimer = useCallback(() => {
    if (completeTimerRef.current) {
      clearTimeout(completeTimerRef.current);
      completeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearPollTimer();
      clearCompleteTimer();
    };
  }, [clearPollTimer, clearCompleteTimer]);

  useEffect(() => {
    clearPollTimer();
    clearCompleteTimer();
    failedNotifiedRef.current = false;
    setPdfReady(false);
    setPdfGenerating(false);
    setPrintProgress(0);

    const excelName = csvName?.trim();
    if (!excelName || isMarkingLoading) {
      return;
    }

    let cancelled = false;

    const pollOnce = async (): Promise<boolean> => {
      const response = await getResultPdfStatus(excelName);
      if (cancelled) {
        return true;
      }

      const data = response.data;
      if (data.ready) {
        setPdfReady(true);
        setPrintProgress(100);
        clearCompleteTimer();
        completeTimerRef.current = setTimeout(() => {
          setPdfGenerating(false);
          setPrintProgress(0);
        }, 1000);
        return true;
      }

      if (data.status === "failed") {
        setPdfReady(false);
        setPdfGenerating(false);
        setPrintProgress(0);
        if (!failedNotifiedRef.current) {
          failedNotifiedRef.current = true;
          onErrorRef.current?.(data.error || "個票PDFの生成に失敗しました。");
        }
        return true;
      }

      setPdfGenerating(true);
      setPrintProgress(PDF_PROGRESS_PROCESSING);
      return false;
    };

    const tick = async () => {
      try {
        if (await pollOnce()) {
          clearPollTimer();
        }
      } catch (error) {
        console.log(error);
        clearPollTimer();
        setPdfGenerating(false);
        onErrorRef.current?.("個票PDFの状態確認に失敗しました。");
      }
    };

    setPdfGenerating(true);
    setPrintProgress(PDF_PROGRESS_PROCESSING);
    tick();
    pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearPollTimer();
      clearCompleteTimer();
    };
  }, [clearPollTimer, clearCompleteTimer, csvName, isMarkingLoading]);

  const handlePrint = useCallback(async () => {
    const excelName = csvName?.trim();
    if (!excelName || !pdfReady) {
      return;
    }

    setPrintLoading(true);
    try {
      const response = await downloadResultPdf(excelName);
      if (response.data?.url) {
        window.open(response.data.url, "_blank", "noopener,noreferrer");
        onSuccessRef.current?.();
      }
    } catch (error: any) {
      console.log(error);
      const detail =
        error?.response?.data?.detail || "個票PDFの表示に失敗しました。";
      onErrorRef.current?.(
        typeof detail === "string" ? detail : "個票PDFの表示に失敗しました。"
      );
    } finally {
      setPrintLoading(false);
    }
  }, [csvName, pdfReady]);

  const canPrint = !isMarkingLoading && !!csvName && pdfReady && !isPrintLoading;

  return {
    handlePrint,
    isPrintLoading,
    isPdfGenerating,
    printProgress,
    canPrint,
    pdfReady,
  };
};

export default usePrintStudentSheets;
